<script setup lang="ts">
/* eslint-disable vue/no-undef-components */
import { useAiTools } from '@/composables/useAiTools';
import { provideSeoDetailLanguagePaths } from '@/composables/useSeoDetailLanguageNavigation';
import { useSeoLocaleAvailability } from '@/composables/useSeoLocaleAvailability';
import { computed } from 'vue';
import AppFooter from '@/components/AppFooter.vue';
import AiToolsSeoContent from '@/components/ai-tools/AiToolsSeoContent.vue';
import ImageLazy from '@/components/ImageLazy.vue';
import { t } from '@/i18n';
import {
  TOOL_CATEGORY_DEFINITIONS,
  toolCategoryByValue,
  toolCategoryMorePath,
  type ToolCategoryValue,
} from '@/lib/tool-taxonomy';
import {
  absoluteLocalizedUrl,
  buildPathHreflangLinks,
  localizedPath,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';

const props = defineProps<{
  category?: ToolCategoryValue;
}>();

const { fetchAllTools } = useAiTools();
const { fetchAvailableLocales } = useSeoLocaleAvailability();
const route = useRoute();
const router = useRouter();
const locale = computed(() => siteLocaleFromRouteParam(route.params.locale));
const selectedCategory = computed(() => toolCategoryByValue(props.category));
const ITEMS_PER_PAGE = 9;
const currentPage = computed(() => {
  const rawPage = Array.isArray(route.params.num) ? route.params.num[0] : route.params.num;
  const parsed = Number.parseInt(String(rawPage || '1'), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
});
const categoryIndexPath = computed(() =>
  selectedCategory.value ? toolCategoryMorePath(selectedCategory.value) : '/ai-design-tools'
);
const sectionPath = computed(() =>
  selectedCategory.value
    ? currentPage.value > 1
      ? `${categoryIndexPath.value}/page/${currentPage.value}`
      : categoryIndexPath.value
    : '/ai-design-tools'
);
const isToolsRoot = computed(() => !selectedCategory.value);

const { data: toolsData } = await useAsyncData(
  () => `ai-tools-all:${locale.value}:${props.category || 'all'}`,
  async () => {
    try {
      const tools = await fetchAllTools(locale.value, {
        category: props.category,
        preview: isToolsRoot.value,
      });
      return tools || [];
    } catch (error) {
      console.error('Error fetching tools:', error);
      return [];
    }
  },
  {
    server: true,
    lazy: false,
    default: () => [],
    watch: [locale, selectedCategory],
  }
);
const { data: availableLocales } = await useAsyncData(
  'seo-locale-availability:ai-design-tools',
  () => fetchAvailableLocales('ai-design-tools'),
  {
    server: true,
    lazy: false,
    default: () => [],
  }
);

const groupedTools = computed(() => {
  if (!Array.isArray(toolsData.value)) return [];

  const categoryMap = new Map<string, any[]>();
  for (const tool of toolsData.value) {
    if (!tool || !tool.slug || typeof tool.slug !== 'string' || tool.slug.trim() === '') continue;
    const category = tool.category || 'Business & Print';
    if (!categoryMap.has(category)) {
      categoryMap.set(category, []);
    }
    categoryMap.get(category)!.push({
      slug: String(tool.slug).trim(),
      title:
        tool.sceneKeywords || tool.heroTitle || t('landingPages.aiToolsIndex.card.fallback_title'),
      description: tool.subtitle || '',
      category,
      image: tool.image || '/Veeso_og.jpg',
    });
  }

  const orderedCategories = [
    ...TOOL_CATEGORY_DEFINITIONS.map(({ value }) => value).filter((cat) => categoryMap.has(cat)),
    ...[...categoryMap.keys()]
      .filter((category) => !toolCategoryByValue(category))
      .sort((a, b) => a.localeCompare(b)),
  ];

  return orderedCategories.map((category) => {
    const definition = toolCategoryByValue(category);
    return {
      id: definition?.slug || category.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      title: definition ? t(definition.labelKey) : category,
      morePath: definition ? toolCategoryMorePath(definition) : null,
      items: categoryMap.get(category)!,
    };
  });
});

const hasTools = computed(() => groupedTools.value.some((group) => group.items.length > 0));
const categoryItems = computed(() => groupedTools.value[0]?.items ?? []);
const totalItems = computed(() => categoryItems.value.length);
const totalPages = computed(() => Math.max(1, Math.ceil(totalItems.value / ITEMS_PER_PAGE)));
const startIndex = computed(() => (currentPage.value - 1) * ITEMS_PER_PAGE);
const endIndex = computed(() => Math.min(startIndex.value + ITEMS_PER_PAGE, totalItems.value));
const displayedGroupItems = (group: { items: any[] }) =>
  isToolsRoot.value ? group.items : group.items.slice(startIndex.value, endIndex.value);
const pageNumbers = computed(() => {
  const pages: Array<number | string> = [];
  if (totalPages.value <= 7) {
    for (let page = 1; page <= totalPages.value; page++) pages.push(page);
    return pages;
  }

  pages.push(1);
  if (currentPage.value > 3) pages.push('…');
  const firstNearbyPage = Math.max(2, currentPage.value - 1);
  const lastNearbyPage = Math.min(totalPages.value - 1, currentPage.value + 1);
  for (let page = firstNearbyPage; page <= lastNearbyPage; page++) pages.push(page);
  if (currentPage.value < totalPages.value - 2) pages.push('…');
  pages.push(totalPages.value);
  return pages;
});

function categoryPagePath(page: number) {
  return page === 1 ? categoryIndexPath.value : `${categoryIndexPath.value}/page/${page}`;
}

function goToPage(page: number) {
  if (!selectedCategory.value || page < 1 || page > totalPages.value) return;
  void router.push(localizedPath(locale.value, categoryPagePath(page)));
  if (import.meta.client) window.scrollTo({ top: 0, behavior: 'smooth' });
}

function previousPage() {
  goToPage(currentPage.value - 1);
}

function nextPage() {
  goToPage(currentPage.value + 1);
}

if (
  selectedCategory.value &&
  route.params.num &&
  (currentPage.value < 2 || currentPage.value > totalPages.value)
) {
  throw createError({ statusCode: 404, statusMessage: 'Tool category page not found' });
}

const headLocales = computed(() =>
  availableLocales.value.length ? availableLocales.value : hasTools.value ? [locale.value] : []
);
const languagePaths = computed(() =>
  Object.fromEntries(
    headLocales.value.map((availableLocale) => [
      availableLocale,
      localizedPath(availableLocale, sectionPath.value),
    ])
  )
);
provideSeoDetailLanguagePaths(languagePaths);
const hasCurrentLanguageContent = computed(
  () => hasTools.value && headLocales.value.includes(locale.value)
);
const moreTitle = computed(() => t('ui.tools.more'));
const pageTitle = computed(() => {
  const category = selectedCategory.value;
  if (!category) return t('landingPages.aiToolsIndex.hero.title');
  return t('ui.tools.categoryPageTitle', { category: t(category.labelKey) });
});
const pageSubtitle = computed(() => t('landingPages.aiToolsIndex.hero.subtitle'));
const seoTitle = computed(() =>
  selectedCategory.value
    ? currentPage.value > 1
      ? t('landingPages.blog.index.seo.page_title', {
          title: pageTitle.value,
          page: currentPage.value,
        })
      : `${pageTitle.value} | Veeso`
    : t('landingPages.aiToolsIndex.seo.title')
);
const seoDescription = computed(() =>
  selectedCategory.value
    ? `${t(selectedCategory.value.labelKey)}: ${t('landingPages.aiToolsIndex.seo.description')}`
    : t('landingPages.aiToolsIndex.seo.description')
);
const seoKeywords = computed(() =>
  selectedCategory.value
    ? `${t(selectedCategory.value.labelKey)}, ${t('landingPages.aiToolsIndex.seo.keywords')}`
    : t('landingPages.aiToolsIndex.seo.keywords')
);
const canonicalUrl = computed(() => absoluteLocalizedUrl(locale.value, sectionPath.value));

// OG Image URL - use static resource from public folder
const requestURL = useRequestURL();
const ogImageUrl = computed(() => {
  const baseUrl = requestURL.origin;
  return `${baseUrl}/Veeso_og.jpg`;
});

useHead(() => {
  const links: Array<{ rel: string; href: string }> = [
    { rel: 'canonical', href: canonicalUrl.value },
  ];

  if (hasCurrentLanguageContent.value) {
    links.push(...buildPathHreflangLinks(sectionPath.value, 'https://veeso.ai', headLocales.value));
  }

  if (selectedCategory.value && currentPage.value > 1) {
    links.push({
      rel: 'prev',
      href: absoluteLocalizedUrl(locale.value, categoryPagePath(currentPage.value - 1)),
    });
  }
  if (selectedCategory.value && currentPage.value < totalPages.value) {
    links.push({
      rel: 'next',
      href: absoluteLocalizedUrl(locale.value, categoryPagePath(currentPage.value + 1)),
    });
  }

  return {
    title: seoTitle.value,
    htmlAttrs: { lang: locale.value },
    meta: [
      {
        name: 'robots',
        content: hasCurrentLanguageContent.value ? 'index, follow' : 'noindex, follow',
      },
      {
        name: 'description',
        content: seoDescription.value,
      },
      {
        name: 'keywords',
        content: seoKeywords.value,
      },
      { property: 'og:title', content: seoTitle.value },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: canonicalUrl.value },
      { property: 'og:locale', content: ogLocaleFor(locale.value) },
      {
        property: 'og:description',
        content: seoDescription.value,
      },
      { property: 'og:image', content: ogImageUrl.value },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: seoTitle.value },
      { name: 'twitter:description', content: seoDescription.value },
      { name: 'twitter:image', content: ogImageUrl.value },
    ],
    link: links,
  };
});

// Scroll functionality
const scrollContainer = (id: string, direction: 'left' | 'right', event?: Event) => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  const container = document.getElementById(id);
  if (container) {
    const scrollAmount = 320; // Width of card + gap
    if (direction === 'left') {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }
};
</script>

<template>
  <div class="ai-design-tools-page min-h-screen bg-white" :data-locale="locale">
    <AppNav :active-path="sectionPath" />

    <!-- Hero Section -->
    <section class="relative pt-48 pb-32 overflow-hidden ai-design-tools-hero">
      <div class="relative container mx-auto px-6 md:px-4 text-center z-10">
        <h1
          class="font-boldonse text-4xl md:text-6xl text-white uppercase tracking-tight mb-8 drop-shadow-sm"
        >
          {{ pageTitle }}
        </h1>
        <h2
          class="font-boldonse text-2xl md:text-3xl normal-case font-normal mt-4 text-white drop-shadow-sm"
        >
          {{ pageSubtitle }}
        </h2>
      </div>
    </section>

    <div class="container mx-auto px-4 py-16 relative z-10">
      <div class="max-w-[1280px] mx-auto mb-16">
        <!-- Grouped Tools List -->
        <div v-if="groupedTools.length > 0" class="space-y-16">
          <section v-for="group in groupedTools" :key="group.id" class="tool-category-section">
            <div class="flex items-center justify-between mb-8 px-1">
              <h2 class="text-2xl md:text-3xl font-bold text-black/90 tracking-tight">
                {{ group.title }}
              </h2>
              <NuxtLink
                v-if="isToolsRoot && group.morePath"
                :to="localizedPath(locale, group.morePath)"
                class="inline-flex items-center gap-2 text-sm font-semibold text-black/60 no-underline transition-colors hover:text-black"
              >
                {{ moreTitle }}
                <span aria-hidden="true">→</span>
              </NuxtLink>
            </div>

            <div
              :id="`tool-section-${group.id}`"
              :class="
                isToolsRoot
                  ? 'flex gap-5 overflow-x-auto pb-8 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide snap-x snap-mandatory'
                  : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              "
              :style="isToolsRoot ? { touchAction: 'pan-x' } : undefined"
            >
              <NuxtLink
                v-for="tool in displayedGroupItems(group)"
                :key="tool.slug"
                :to="localizedPath(locale, `/ai-design-tools/${tool.slug}`)"
                class="group cursor-pointer bg-white rounded-[20px] border border-black/5 hover:border-black/15 transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_24px_rgba(0,0,0,0.12)] overflow-hidden block no-underline"
                :class="isToolsRoot ? 'flex-shrink-0 w-[280px] md:w-[300px] snap-start' : 'w-full'"
                @click="
                  (e) => {
                    if (!tool.slug) {
                      e.preventDefault();
                      console.error('Invalid slug for tool:', tool);
                    }
                  }
                "
              >
                <div
                  class="h-44 relative overflow-hidden bg-gradient-to-br from-black/5 to-black/10"
                >
                  <ImageLazy
                    :src="tool.image"
                    :alt="tool.title"
                    class="w-full h-full"
                    image-class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    width="100%"
                    height="100%"
                    loading="lazy"
                    root-margin="320px"
                  />
                  <div
                    class="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  ></div>
                </div>

                <div class="p-6 flex flex-col min-h-[200px]">
                  <div class="flex-1 mb-4">
                    <h3
                      class="text-xl font-bold text-black/90 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors leading-tight"
                    >
                      {{ tool.title }}
                    </h3>
                    <p class="text-sm text-black/60 font-normal leading-relaxed line-clamp-3">
                      {{ tool.description }}
                    </p>
                  </div>

                  <div class="pt-4 border-t border-black/5">
                    <div
                      class="inline-flex items-center text-sm font-semibold text-black/70 group-hover:text-blue-600 transition-colors"
                    >
                      {{ t('landingPages.aiToolsIndex.card.cta') }}
                      <svg
                        class="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </NuxtLink>
            </div>

            <div
              v-if="isToolsRoot && group.items.length > 3"
              class="mt-1 flex justify-center gap-3"
            >
              <button
                @click="scrollContainer(`tool-section-${group.id}`, 'left', $event)"
                class="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center hover:bg-black/5 transition-colors"
                :aria-label="t('landingPages.aiToolsIndex.scroll.left_aria')"
                type="button"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
              <button
                @click="scrollContainer(`tool-section-${group.id}`, 'right', $event)"
                class="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center hover:bg-black/5 transition-colors"
                :aria-label="t('landingPages.aiToolsIndex.scroll.right_aria')"
                type="button"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            </div>
          </section>

          <nav
            v-if="!isToolsRoot && totalPages > 1"
            class="flex items-center justify-center gap-2 mt-12"
            :aria-label="t('ui.a11y.pagination')"
          >
            <button
              type="button"
              class="px-4 py-2 rounded-lg bg-white text-black/70 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5"
              :disabled="currentPage === 1"
              :aria-label="t('landingPages.blog.index.pagination.prev_aria')"
              @click="previousPage"
            >
              {{ t('landingPages.blog.index.pagination.prev') }}
            </button>

            <div class="flex items-center gap-1">
              <button
                v-for="(pageNumber, index) in pageNumbers"
                :key="`tools-page-${index}`"
                type="button"
                class="min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 disabled:cursor-default"
                :class="
                  pageNumber === currentPage
                    ? 'bg-black/90 text-white'
                    : typeof pageNumber === 'string'
                      ? 'text-black/30 cursor-default'
                      : 'bg-white text-black/70 hover:bg-black/5'
                "
                :disabled="typeof pageNumber === 'string'"
                :aria-current="pageNumber === currentPage ? 'page' : undefined"
                :aria-label="
                  typeof pageNumber === 'number'
                    ? t('ui.a11y.goToPage', { page: String(pageNumber) })
                    : undefined
                "
                @click="typeof pageNumber === 'number' ? goToPage(pageNumber) : undefined"
              >
                {{ pageNumber }}
              </button>
            </div>

            <button
              type="button"
              class="px-4 py-2 rounded-lg bg-white text-black/70 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5"
              :disabled="currentPage === totalPages"
              :aria-label="t('landingPages.blog.index.pagination.next_aria')"
              @click="nextPage"
            >
              {{ t('landingPages.blog.index.pagination.next') }}
            </button>
          </nav>

          <p v-if="!isToolsRoot && totalItems > 0" class="text-center mt-6 text-sm text-black/50">
            {{
              t('landingPages.blog.index.showing_posts', {
                start: startIndex + 1,
                end: endIndex,
                total: totalItems,
              })
            }}
          </p>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-24">
          <p class="text-lg text-black/60">{{ t('landingPages.aiToolsIndex.empty_state') }}</p>
        </div>

        <!-- SEO Content -->
        <div v-if="isToolsRoot && hasTools" class="mt-12 border-t border-black/5 pt-16">
          <AiToolsSeoContent />
        </div>
      </div>
    </div>
    <AppFooter />
  </div>
</template>

<style scoped>
.ai-design-tools-hero {
  background: linear-gradient(
    to bottom,
    #94b3da 0%,
    #d0e3f6 15%,
    #f5f1e6 30%,
    #e8d5c3 45%,
    #97a4bc 60%,
    #395e94 75%,
    #08306f 90%,
    #02245d 100%
  );
}

.ai-design-tools-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 200px 200px;
  mix-blend-mode: overlay;
  opacity: 0.3;
  z-index: 1;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.scrollbar-hide {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

/* Keep every SEO link in the server HTML while deferring off-screen layout and paint. */
.tool-category-section {
  content-visibility: auto;
  contain-intrinsic-size: auto 520px;
}
</style>
